// src/Stream/streamClient.js
// This includes the live code with credential check.
// src/Stream/streamClient.js

import { useOptionsStore } from '../Store/optionsStore';
import usePlaybackStore from '../Store/playbackStore';

const WS_URL = process.env.REACT_APP_OPTION_FLOWS_WEBSOCKET_URL || 'wss://option-flow-widgets-wss-a980c94675fd.herokuapp.com/ws';

export function startStream({ memberCredentials = null } = {}) {
  const store = useOptionsStore.getState();

  const {
    setStatus,
    recordObservation,
    applyInit,
    applyRow,
    applyAggSnapshot,
    applyAggPatch,
  } = store;

  let ws = null;
  let retryTimer = null;
  let backoff = 1000;

  let gotAggSnapshot = false;
  let keepBars = 125;

  const patchQueue = [];
  const MAX_QUEUE = 500;

  function isReplaying() {
    return usePlaybackStore.getState().mode === 'playing';
  }

  function applyStatus(status) {
    if (isReplaying()) usePlaybackStore.getState().queueLiveOptionStatus(status);
    else setStatus?.(status);
  }

  function flushPatches() {
    if (!applyAggPatch) return;

    while (patchQueue.length) {
      const msg = patchQueue.shift();

      try {
        if (isReplaying()) usePlaybackStore.getState().queueLiveAggPatch(msg, keepBars);
        else applyAggPatch(msg, keepBars);
      } catch (error) {
        console.error('[STREAM] Failed to apply queued agg patch:', error);
      }
    }
  }

  function onMessage(event) {
    let msg;

    try {
      msg = JSON.parse(event.data);
    } catch {
      return;
    }

    switch (msg?.type) {
      case 'init':
        if (Array.isArray(msg.rows)) {
          if (isReplaying()) usePlaybackStore.getState().queueLiveOptionInit(msg.rows);
          else {
            applyInit?.(msg.rows);
            if (msg.rows.length) recordObservation?.(Date.now());
          }
        }
        return;

      case 'row':
        if (isReplaying()) usePlaybackStore.getState().queueLiveOptionRow(msg);
        else {
          applyRow?.(msg);
          if (msg.sym) recordObservation?.(Date.now());
        }
        return;

      case 'init_agg': {
        if (!applyAggSnapshot) return;

        const lookback = Number(msg.lookback) || 120;
        const warmup = Number(msg.warmup) || 5;

        keepBars = Math.max(lookback + warmup, 1);

        const payload = {
          minuteAgg: msg.minuteAgg || {},
          spot: msg.spot ?? null,
        };
        if (isReplaying()) {
          usePlaybackStore.getState().queueLiveAggSnapshot(payload, keepBars);
        } else {
          applyAggSnapshot(payload, keepBars);
        }

        gotAggSnapshot = true;
        flushPatches();
        return;
      }

      case 'minute_agg_patch': {
        if (!applyAggPatch) return;

        if (!gotAggSnapshot) {
          if (patchQueue.length >= MAX_QUEUE) {
            patchQueue.shift();
          }

          patchQueue.push(msg);
          return;
        }

        if (isReplaying()) usePlaybackStore.getState().queueLiveAggPatch(msg, keepBars);
        else applyAggPatch(msg, keepBars);
        return;
      }

      default:
        return;
    }
  }

  function authenticate() {
    if (!memberCredentials?.memberId || !memberCredentials?.status) {
      return;
    }

    ws.send(
      JSON.stringify({
        type: 'authenticate',
        data: {
          memberId: memberCredentials.memberId,
          status: memberCredentials.status,
          planId: memberCredentials.planId,
        },
      })
    );
  }

  function open() {
    applyStatus('connecting');

    try {
      ws = new WebSocket(WS_URL);
    } catch (error) {
      console.error('[STREAM] Failed to create WebSocket:', error);
      applyStatus('disconnected');
      return;
    }

    ws.onopen = () => {
      backoff = 1000;
      applyStatus('connected');
      console.log('[STREAM] Connected to option flow WSS');

      authenticate();
    };

    ws.onmessage = onMessage;

    ws.onerror = (error) => {
      console.error('[STREAM] WebSocket error:', error);
    };

    ws.onclose = () => {
      applyStatus('disconnected');

      ws = null;
      gotAggSnapshot = false;
      patchQueue.length = 0;

      const retryDelay = Math.min(10000, backoff);
      backoff = Math.floor(backoff * 1.6);

      retryTimer = setTimeout(open, retryDelay);
    };
  }

  open();

  return () => {
    if (retryTimer) {
      clearTimeout(retryTimer);
    }

    if (ws) {
      try {
        ws.close();
      } catch (error) {
        console.error('[STREAM] Failed to close WebSocket:', error);
      }
    }

    if (isReplaying()) usePlaybackStore.getState().queueLiveOptionStatus('disconnected');
    else useOptionsStore.getState().setStatus?.('disconnected');
  };
}


